import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
    Loader2, Calendar, Building2, Clock,
    Search, ChevronLeft, ChevronRight,
    Files,
    File,
    XCircle,
    Users
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { cancelVisit, getVisitsByUser } from '@/lib/collections/visits/user/getVisitFetch';
import { toast } from 'react-toastify';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { fetchUsersWithProfiles } from '@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile';

interface Visit {
    $id: string;
    organizationId: string;
    organizationName?: string; // Lo traeremos del join
    status: string;
    visitDates: string[];
    visitPurpose: string;
    $createdAt: string;
    branch?: string | null;
    usersId?: string[] | null;

}

interface VisitsListProps {
    userId: string;
}

const VisitsList = ({ userId }: VisitsListProps) => {
    const [visits, setVisits] = useState<Visit[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [_hasMoreRecords, setHasMoreRecords] = useState(false);
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const [cancelingId, setCancelingId] = useState<string | null>(null);
    const [cancelReason, setCancelReason] = useState('');
    const [usersMap, setUsersMap] = useState<Map<string, string>>(new Map());

    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);

    const [page, setPage] = useState(1);
    const limit = 12;



    const getYearOptions = () => {
        const options = [];
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push({ value: year.toString(), label: year.toString() });
        }
        return options;
    };

    const getMonthOptions = () => {
        return [
            { value: '01', label: 'Enero' },
            { value: '02', label: 'Febrero' },
            { value: '03', label: 'Marzo' },
            { value: '04', label: 'Abril' },
            { value: '05', label: 'Mayo' },
            { value: '06', label: 'Junio' },
            { value: '07', label: 'Julio' },
            { value: '08', label: 'Agosto' },
            { value: '09', label: 'Septiembre' },
            { value: '10', label: 'Octubre' },
            { value: '11', label: 'Noviembre' },
            { value: '12', label: 'Diciembre' },

        ];
    };


    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        fetchUsersWithProfiles().then(users => {
            const map = new Map(users.map(u => [u.id, u.name]));
            setUsersMap(map);
        });
    }, []);

    // Helper
    const getUserName = (id: string) => usersMap.get(id) || id;

    useEffect(() => {
        loadVisits();
    }, [userId, page, debouncedSearch, statusFilter, selectedYear, selectedMonth]);
    const loadVisits = async () => {
        try {
            setLoading(true);
            const result = await getVisitsByUser({
                userId,
                page,
                limit,
                searchQuery: debouncedSearch,
                statusFilter,
                yearFilter: selectedYear,      // NUEVO
                monthFilter: selectedMonth      // NUEVO
            });
            setVisits(result.documents);
            setTotal(result.total);
            setHasMoreRecords(result.hasMoreRecords || false); // Agrega esto
        } catch (error: any) {
            console.log(error.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-EC', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'America/Guayaquil'

        });
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('es-EC', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Guayaquil'
        });
    };




    const getStatusBadge = (status: string) => {
        const variants = {
            pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pendiente' },
            completed: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Completada' },
            canceled: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Cancelada' }
        };
        const variant = variants[status as keyof typeof variants] || variants.pending;

        return (
            <Badge className={`${variant.color} border`}>
                {variant.label}
            </Badge>
        );
    };

    const totalPages = Math.ceil(total / limit);

    if (loading && visits.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-100">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const handleCancelVisit = async (visitId: string) => {
        try {
            setCancelingId(visitId);
            await cancelVisit(visitId, cancelReason.trim());
            toast.success('Visita cancelada exitosamente');
            setCancelReason(''); // Limpiar el textarea
            loadVisits(); // Recargar lista
        } catch {
            toast.error('Error al cancelar la visita. Intenta de nuevo.');
        } finally {
            setCancelingId(null);
        }
    };

    return (


        <><Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <div className="flex items-center gap-1.5">
                        <Files />
                        <BreadcrumbPage>Bitácoras</BreadcrumbPage>
                    </div>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                        <Link to="/requests">Menu</Link>
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                    <BreadcrumbPage className="hidden sm:block">Visitas</BreadcrumbPage>
                    <BreadcrumbPage className="block sm:hidden "> <File className="w-5 h-5" /> </BreadcrumbPage>
                </BreadcrumbItem>
            </BreadcrumbList>
        </Breadcrumb>


            <div className="space-y-6 p-6">
                {/* Header con búsqueda y filtros */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">Mis Visitas </h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {total} {total === 1 ? 'visita registrada' : 'visitas registradas'}
                            </p>
                        </div>

                        <Link to="/requests/$id/visit/addRequest" params={{ id: userId }}>
                            <Button className='cursor-pointer shadow-sm hover:shadow-md transition-shadow'>
                                Agregar Registro
                            </Button>
                        </Link>
                    </div>

                    {/* Barra de búsqueda y filtros */}
                    <div className="flex flex-wrap gap-3">
                        {/* Búsqueda */}
                        <div className="relative flex-1 min-w-50">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Buscar por organización u objetivo..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10" />
                            {loading && searchQuery && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                            )}
                        </div>

                        {/* Select Estado */}
                        <Select value={statusFilter} onValueChange={(value) => {
                            setStatusFilter(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                <SelectItem value="pending">Pendiente</SelectItem>
                                <SelectItem value="completed">Completada</SelectItem>
                                <SelectItem value="canceled">Cancelada</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Select Año */}
                        <Select value={selectedYear} onValueChange={(value) => {
                            setSelectedYear(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-32.5">
                                <SelectValue placeholder="Año" />
                            </SelectTrigger>
                            <SelectContent>
                                {getYearOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Select Mes */}
                        <Select value={selectedMonth} onValueChange={(value) => {
                            setSelectedMonth(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-35">
                                <SelectValue placeholder="Mes" />
                            </SelectTrigger>
                            <SelectContent>
                                {getMonthOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {/* Grid de visitas */}
                {visits.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="rounded-full bg-muted p-6">
                                    <Calendar className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-lg font-medium">
                                        {debouncedSearch ? 'No se encontraron visitas' : 'No hay visitas registradas'}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {debouncedSearch
                                            ? 'Intenta con otros términos de búsqueda'
                                            : 'Comienza agregando tu primera visita'}
                                    </p>
                                </div>
                                <Link to="/requests/$id/visit/addRequest" params={{ id: userId }}>
                                    <Button className='cursor-pointer shadow-sm hover:shadow-md transition-shadow'>
                                        Agregar Registro
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {visits.map((visit) => {


                                return (
                                    <Card
                                        key={visit.$id}
                                        className="group hover:shadow-xl  duration-300 cursor-pointer overflow-hidden border-2 "
                                    >
                                        <CardHeader className="to-transparent">
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                                                        <Building2 className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <h3 className="font-semibold text-base line-clamp-1 leading-tight">
                                                        {visit.organizationName || 'Cargando...'}
                                                    </h3>
                                                </div>
                                                {getStatusBadge(visit.status)}
                                            </div>

                                            <Badge
                                                variant="outline"
                                                className="text-xs max-w-35 line-clamp-2"
                                            >
                                                {visit.branch ?? 'Matriz'}
                                            </Badge>

                                            {visit.usersId && visit.usersId.length > 0 && (
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs flex items-center gap-1 w-fit dark:bg-slate-900 bg-slate-50 text-slate-700 border-slate-200"
                                                >
                                                    <Users className="w-3 h-3" />
                                                    {visit.usersId.map(id => getUserName(id)).join(', ')}
                                                </Badge>
                                            )}

                                            <p className="text-sm text-muted-foreground line-clamp-2 min-h-10">
                                                {visit.visitPurpose}
                                            </p>
                                        </CardHeader>

                                        <CardContent className=" pb-4 space-y-3">
                                            {/* Fecha de visita */}
                                            <div className="flex items-center gap-2 text-sm">
                                                <div className="rounded-md bg-blue-500/10 p-1.5">
                                                    <Calendar className="h-3.5 w-3.5 text-blue-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs text-muted-foreground">Fecha</p>
                                                    <p className="font-medium truncate"> {formatDate(visit.visitDates[1])}</p>
                                                </div>
                                            </div>

                                            {/* Horario */}
                                            <div className="flex items-center gap-2 text-sm">
                                                <div className="rounded-md bg-purple-500/10 p-1.5">
                                                    <Clock className="h-3.5 w-3.5 text-purple-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs text-muted-foreground">Horario</p>
                                                    <p className="font-medium">
                                                        {formatTime(visit.visitDates[1])} - {formatTime(visit.visitDates[2])}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Fecha de registro */}
                                            <div className="pt-2 border-t">
                                                <p className="text-xs text-muted-foreground">
                                                    Registrado el {formatDate(visit.visitDates[0])}
                                                </p>
                                            </div>

                                            {/* Botón cancelar (solo si está pendiente) */}
                                            {visit.status === 'pending' && (
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="w-full mt-2 cursor-pointer text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                                                            disabled={cancelingId === visit.$id}
                                                        >
                                                            {cancelingId === visit.$id ? (
                                                                <>
                                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                    Cancelando...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <XCircle className="mr-2 h-4 w-4" />
                                                                    Cancelar visita
                                                                </>
                                                            )}
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent className='font-outfit'>
                                                        <AlertDialogHeader >
                                                            <AlertDialogTitle>¿Cancelar esta visita?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Esta acción no se puede deshacer. La visita será marcada como cancelada.
                                                                <br /><br />
                                                                <div>
                                                                    <Label>Empresa: <span className='text-gray-600'>{visit.organizationName}</span> </Label>
                                                                    <br />
                                                                    <Label>Fecha: <span className='text-gray-600'>{formatDate(visit.visitDates[1])}</span> </Label>
                                                                    <br />
                                                                    <Label>Horario:  <span className='text-gray-600'>{formatTime(visit.visitDates[1])} - {formatTime(visit.visitDates[2])}</span> </Label>

                                                                </div>
                                                                <br />
                                                                <Label>Motivo de cancelación:</Label >
                                                                <br />
                                                                <Textarea
                                                                    value={cancelReason}
                                                                    onChange={(e) => setCancelReason(e.target.value)}
                                                                    placeholder="Describe brevemente el motivo de la cancelación..."
                                                                    className="min-h-25 resize-none"
                                                                    maxLength={999}
                                                                />
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel
                                                                className='cursor-pointer'
                                                                onClick={() => setCancelReason('')}
                                                            >
                                                                No, mantener
                                                            </AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => handleCancelVisit(visit.$id)}
                                                                className="bg-red-600 hover:bg-red-700 cursor-pointer"
                                                                disabled={!cancelReason.trim()}
                                                            >
                                                                Sí, cancelar visita
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            )}

                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 pt-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1 || loading}
                                    className="cursor-pointer"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                        <Button
                                            key={p}
                                            variant={page === p ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setPage(p)}
                                            disabled={loading}
                                            className="cursor-pointer min-w-10"
                                        >
                                            {p}
                                        </Button>
                                    ))}
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || loading}
                                    className="cursor-pointer"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div></>
    );
};




export default VisitsList;